/**
 * AssistantActionModal — the surface a transaction-form `handler='assistant'`
 * action button opens.
 *
 * Hosts the shared AssistantChat seeded per the button's mode:
 *   - 'intent': runs the defined intent DETERMINISTICALLY (no LLM) and shows
 *     its result (visual / report / rows / text / proposed action) — then the
 *     user can follow up in the same chat.
 *   - 'prompt': auto-sends the rendered prompt as the first chat turn.
 *
 * KEPT IN SYNC between frontend/src/assistant and runtime/client/src/assistant
 * (only the AssistantChat import path differs).
 */
import { createPortal } from 'react-dom'
import { useEffect } from 'react'
import type { AssistantActionRequest } from '@twasta/page-runtime/txn'
import { AssistantChat } from './AssistantChat'

export function AssistantActionModal({ project, req, onClose }: {
  project: string
  req: AssistantActionRequest
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return createPortal(
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={hdr}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{req.title}</span>
          <button onClick={onClose} style={xBtn} aria-label="Close" title="Close (Esc)">✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <AssistantChat
            project={project}
            height="100%"
            greeting=""
            suggestedQuestions={[]}
            initialMessage={req.mode === 'prompt' ? req.prompt : undefined}
            initialIntent={req.mode === 'intent' && req.intentId
              ? { intentId: req.intentId, parameters: req.parameters, label: req.title }
              : undefined}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9700,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
}
const sheet: React.CSSProperties = {
  width: 'min(760px, 96vw)', height: 'min(680px, 92vh)', background: '#fff', color: '#1f2937',
  borderRadius: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden',
  boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
}
const hdr: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '11px 14px', borderBottom: '1px solid #e5e7eb',
}
const xBtn: React.CSSProperties = {
  border: 'none', background: 'transparent', fontSize: 17, cursor: 'pointer', color: '#6b7280', padding: 4,
}
